import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MainLogo,
  UserIcon,
  SpaceUsageIcon,
  LogoutIcon,
  CloseMenu,
  Mobilemenu,
  Help,
} from "assets/images";
import {
  getPostpaidOrdersCount,
  orderInProcess,
  pendingApprovalCount,
} from "redux/asyncApi/orderApi";
import { ROUTES } from "routes/constant";
import {
  bytesToSize,
  currencyFormatter,
  notAvailable,
  usersTypes,
} from "util/helpers";
import {
  getBillingDetail,
  getUsedMemory,
  signOut,
} from "redux/asyncApi/appApi";
import { help } from "util/links";
import { navList } from "./sidebarList";
import "./sidebar.scss";
import Avatar from "components/Avatar";
import { OpenInNewWindowIcon } from "@radix-ui/react-icons";

const Sidebar = ({ isToggle, setIsToggle }) => {
  const [route, setRoute] = useState("");

  //Redux state
  const orderCount = useSelector(
    (state) => state.order?.order_In_Process?.order_count
  );
  const { approval_count, postpaid_orders_count } = useSelector(
    (state) => state.order
  );
  const {
    usedMemory,
    userAuth: {
      token,
      user: { name, role, is_subscribe },
      details: { image },
    },
    billingDetails,
  } = useSelector((state) => state.app);

  //Redux action dispatcher
  const dispatch = useDispatch();

  //Ref
  const divRef = useRef(null);
  const buttonRef = useRef(null);

  //Router functions
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  //User roles
  const {
    companyUser,
    user,
    admin,
    superAdmin,
    rootAdmin,
    postpaidRoot,
    postpaidUser,
    postpaidAdmin,
    billingAdmin,
  } = usersTypes;

  //Methods
  useEffect(() => {
    if (!isToggle) {
      buttonRef?.current?.focus();
    }
  }, [isToggle]);

  useEffect(() => {
    if (
      role === postpaidAdmin ||
      role === billingAdmin ||
      role === rootAdmin ||
      role === postpaidRoot
    ) {
      dispatch(pendingApprovalCount(token));
    }
    if (role === superAdmin) {
      dispatch(getPostpaidOrdersCount(token));
    }
    dispatch(orderInProcess(token));
    dispatch(getUsedMemory(token));
  }, [dispatch, token]);

  useEffect(() => {
    if (role === postpaidRoot) {
      dispatch(getBillingDetail(token));
    }
  }, []);

  useEffect(() => {
    if (role === rootAdmin && !is_subscribe) {
      navigate(ROUTES.BILLINGS);
    }
  }, [route]);

  const checkKey = (e) => {
    const currentNode = e.target;
    const lastChild = divRef.current;
    if (currentNode === lastChild && window.innerWidth <= 767) {
      e.preventDefault();
      buttonRef?.current?.focus();
    }
  };

  const sidebarStatus = (status) => {
    setIsToggle(false);
    setRoute(status);
  };

  //Static condition variables
  const disabledClass = !is_subscribe && "disabled";
  const noTabIndex = !is_subscribe ? "-1" : null;

  return (
    <header
      className={`sideBar ${isToggle ? "toggle" : ""}`}
      onKeyDown={checkKey}
    > 
      <div className="mobileHeader">
        <div className="items-center">
          <div md={12}>
            <Link to="/" className="sideBar-Logo">
              <img
                src={MainLogo}
                alt="247 Accessible Documents Logo"
                className="img-fluid logo-desktop"
              />
            </Link>
          </div>
          <div md={12} className="mobile-show block md:hidden">
            <button
              aria-haspopup="menu"
              aria-label={isToggle ? "close navigation" : "open navigation"}
              ref={buttonRef}
              className={`mobileToggle d-none ${isToggle ? "menuClose" : "menuOpen"
                }`}
              onClick={() => {
                setIsToggle(!isToggle);
              }}
            >
              <Mobilemenu
                aria-hidden="true"
                focusable="false"
                className="menu-bar"
                role="img"
              />
              <CloseMenu
                className="menu-close"
                aria-hidden="true"
                focusable="false"
              />
            </button>
          </div>
        </div>
      </div>
      <div className="cust1">
      <div className={`cust2 overflow-y-auto h-[calc(100vh-270px)] mt-2 ${isToggle ? `block` : `hidden md:block`}`}>
        <nav aria-label="Main">
          <ul className="sideBar__list sideBar__listTop ">
            {navList.map(
              ({
                id,
                to,
                label,
                Icon,
                replace,
                roles,
                activeClass,
                activeFor,
                type,
              }) => {
                if (roles.includes(role)) {
                  return (
                    <li
                      className={
                        activeFor.includes(
                          pathname.replace(`/${params.id}`, "/:id")
                        ) ? activeClass : undefined
                      }
                      key={id}
                    >
                      <Link
                        tabIndex={
                          to !== ROUTES.BILLINGS && !is_subscribe ? "-1" : null
                        }
                        className={to !== ROUTES.BILLINGS && disabledClass.toString()}
                        aria-current={pathname === to ? true : false}
                        onClick={() => sidebarStatus(to)}
                        to={is_subscribe && to}
                        replace={replace}
                      >
                        <span className="sidebar__icon">
                          <Icon aria-hidden="true" focusable="false" />
                        </span>
                        <span className="sidebar__text">{label}</span>
                        {type === "order-management" && (
                          <span className="ms-auto text-xs font-medium me-2 px-2.5 py-0.5 rounded-full bg-primary text-white">{orderCount}</span>
                        )}
                        {type === "requests" && (
                          <span className="ms-auto text-xs font-medium me-2 px-2.5 py-0.5 rounded-full bg-primary text-white">
                            {approval_count > 0 && approval_count}
                          </span>
                        )}
                        {type === "clients-order-management" && (
                          <span className="ms-auto text-xs font-medium me-2 px-2.5 py-0.5 rounded-full bg-primary text-white">
                            {postpaid_orders_count > 0 && postpaid_orders_count}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                }
              }
            )}
          </ul>
        </nav>

        {role !== superAdmin && (
          <div className="spaceCardWrapper">
            <div className="spaceCard flex justify-content-around items-center flex-wrap">
              <div className="spaceCard__left">
                <SpaceUsageIcon aria-hidden="true" focusable="false" />
              </div>
              <div className="spaceCard__Right">
                <h2 className="title">Space Usage</h2>
              </div>
              <div className="spaceCard_bottom">
                {role === admin ||
                  role === rootAdmin ||
                  role === postpaidRoot ||
                  role === postpaidAdmin ||
                  role === billingAdmin ? (
                  <>
                    <p className="mb-0">
                      <span>Self:</span>
                      {usedMemory?.self ? bytesToSize(usedMemory?.self) : "0KB"}
                    </p>
                    <p className="mb-0">
                      <span> Others:</span>
                      {usedMemory?.sub_user
                        ? bytesToSize(usedMemory?.sub_user)
                        : "0KB"}
                    </p>
                  </>
                ) : role === companyUser ||
                  role === user ||
                  role === postpaidUser ? (
                  <p className="mb-0">
                    <span> Self:</span>
                    {usedMemory?.self ? bytesToSize(usedMemory?.self) : "0KB"}
                  </p>
                ) : (
                  <p className="mb-0">
                    <span> Super Admin:</span>
                    {usedMemory?.user ? bytesToSize(usedMemory?.user) : "0KB"}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {role === postpaidRoot && (
          <div className="invoicesCard">
            <div className="spaceCard">
              <h2 className="text-left">Invoice Status</h2>
              <div className="innerInvoicecard">
                <p>
                  <span>Paid: </span>
                  {currencyFormatter(billingDetails?.Invoice_paid)}
                </p>
                <p>
                  <span>Pending: </span>
                  {currencyFormatter(billingDetails?.Invoice_pending_amount)}
                </p>
              </div>
            </div>
          </div>
        )}
        
      </div>
      <nav aria-label="UserAccount" className={`cust3 absolute bottom-0 right-0 left-0 ${isToggle ? `block` : `hidden md:block`}`}>
          <ul className="sideBar__list mt-5">
            <li
              className={
                (pathname === ROUTES.PROFILE ||
                  pathname === ROUTES.EDIT_PROFILE) ?
                  "active" : undefined
              }
            >
              <Link
                tabIndex={noTabIndex}
                className={disabledClass ? disabledClass : undefined}
                to={is_subscribe && ROUTES.PROFILE}
                aria-current={
                  pathname === ROUTES.PROFILE ||
                    pathname === ROUTES.EDIT_PROFILE
                    ? true
                    : false
                }
                onClick={() => sidebarStatus("profile")}
                replace
              >
                {image ? (
                  // <img
                  //   className="img-fluid table_picture me-2"
                  //   src={image}
                  //   alt="User profile img"
                  // />
                  <div className="pr-2">
                    <Avatar imageSrc={image} name={name} />
                  </div>
                ) : (
                  <span className="sidebar__icon">
                    <UserIcon aria-hidden="true" focusable="false" />
                  </span>
                )}
                <span className="sidebar__text capitalize line-clamp">
                  {name}
                </span>
              </Link>
            </li>
            <li
              className={route === "logout" ? "active" : undefined}
            >
              <a href={help} target="_blank" rel="noreferrer" >
                <span className="sidebar__icon text-center">
                  <Help
                    aria-hidden="true"
                    focusable="false"
                    width={20}
                  />
                </span>
                <span className="sidebar__text">Help</span>
                <OpenInNewWindowIcon
                  focusable="false"
                  width={20}
                  role="img"
                  aria-label="opens in a new tab"
                 />
              </a>
            </li>
            <li
              className={route === "logout" ? "active" : undefined}
            >
              <button
                aria-current={route === "logout" ? true : false}
                ref={divRef}
                onClick={() => {
                  sidebarStatus("logout");
                  dispatch(signOut({ token }));
                }}
                className="logout"
              >
                <span className="sidebar__icon">
                  <LogoutIcon aria-hidden="true" focusable="false" />
                </span>
                <span className="sidebar__text">Logout</span>
              </button>
            </li>
          </ul>
        </nav>
        </div>
    </header>
  );
};

export default Sidebar;
